import React, { useEffect, useMemo, useState } from 'react';
import QueryString from 'query-string';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AppsIcon from '@mui/icons-material/Apps';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import ListIcon from '@mui/icons-material/List';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import Loading from '../../components/shared/Loading';
import Page from '../Page';
import ServerError from '../error/ServerError';
import AssignTrainingPlanDialog from './AssignTrainingPlanDialog';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  CREATE_TRAINING_PLAN,
  DELETE_TRAINING_PLAN,
  GET_TRAINING_PLAN_LIST,
  UPDATE_TRAINING_PLAN,
  UPDATE_TRAINING_PLAN_LIST,
} from '../../graphql/training_plan';
import { useSnackbar } from 'notistack';
import config from '../../config';

const statusMapping = { 0: 'Draft', 1: 'Published', 2: 'Private' };
const levelMapping = { 0: 'Beginner', 1: 'Intermediate', 2: 'Advanced' };

const statusColor = (status) => {
  if (`${status}` === '0') return { color: '#b7791f', bg: '#fff3d8' };
  if (`${status}` === '2') return { color: '#40536b', bg: '#eef2f7' };
  return { color: '#118847', bg: '#dcf7e7' };
};

const completionPercent = (plan) => {
  if (!plan.enrolled) return null;
  if (plan.completed == null) return null;
  return Math.min(100, Math.round((plan.completed / plan.enrolled) * 100));
};

const weekCount = (plan) => plan.weekCount || null;

const NoPlanImage = () => (
  <Box
    sx={{
      alignItems: 'center',
      bgcolor: '#f1f5f9',
      color: '#64748b',
      display: 'flex',
      fontSize: 13,
      fontWeight: 700,
      height: '100%',
      justifyContent: 'center',
      width: '100%',
    }}
  >
    No cover image
  </Box>
);

const StatCard = ({ icon, value, label, color, backgroundColor }) => (
  <Card variant="outlined" sx={{ borderColor: '#e2e8f0', borderRadius: 1 }}>
    <CardContent sx={{ py: 2.25, '&:last-child': { pb: 2.25 } }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: '50%',
            color,
            backgroundColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: 25, lineHeight: 1 }}>
            {value}
          </Typography>
          <Typography sx={{ color: '#40536b', fontSize: 14 }}>
            {label}
          </Typography>
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const CompletionRing = ({ value }) => {
  if (value == null) {
    return (
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          border: '3px solid #e2e8f0',
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: '50%',
        background: `conic-gradient(#35b96f ${value * 3.6}deg, #e2e8f0 0deg)`,
        position: 'relative',
        '&:after': {
          content: '""',
          position: 'absolute',
          inset: 4,
          borderRadius: '50%',
          backgroundColor: '#fff',
        },
      }}
    />
  );
};

const TrainingPlanCard = ({
  plan,
  featured,
  onDelete,
  onAddToFeatured,
  onRemoveFromFeatured,
  onPublish,
  publishing,
  onAssign,
}) => {
  const navigate = useNavigate();
  const status = statusMapping[plan.status] ?? 'Published';
  const colors = statusColor(plan.status);
  const completion = completionPercent(plan);
  const isDraft = `${plan.status}` === '0';
  const isPrivate = `${plan.status}` === '2';
  const image = plan.coverImageUrl;

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: '#e2e8f0',
        borderRadius: 1,
        overflow: 'hidden',
        height: '100%',
        boxShadow: '0 8px 20px rgba(15, 23, 42, 0.04)',
      }}
    >
      <Box
        sx={{ position: 'relative', height: 148, backgroundColor: '#111827' }}
      >
        {image ? (
          <Box
            component="img"
            src={image}
            alt={plan.title}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <NoPlanImage />
        )}
        <IconButton
          size="small"
          onClick={onDelete}
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            backgroundColor: '#fff',
            borderRadius: 1,
            '&:hover': { backgroundColor: '#f8fafc' },
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Stack
          direction="row"
          spacing={0.75}
          sx={{ position: 'absolute', left: 12, bottom: 10 }}
        >
          <Chip
            size="small"
            label={status}
            sx={{
              color: colors.color,
              backgroundColor: colors.bg,
              fontWeight: 800,
            }}
          />
          {featured && (
            <Chip
              size="small"
              icon={<StarBorderIcon sx={{ fontSize: '14px !important' }} />}
              label="Featured"
              sx={{
                color: '#4338ca',
                backgroundColor: '#ebe9ff',
                fontWeight: 800,
              }}
            />
          )}
        </Stack>
      </Box>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Typography noWrap sx={{ fontWeight: 800, fontSize: 19, mb: 0.75 }}>
          {plan.title}
        </Typography>
        <Typography sx={{ color: '#40536b', fontSize: 14, mb: 2 }}>
          {levelMapping[plan.level] ?? 'All Levels'} • {weekCount(plan) ?? '-'}{' '}
          Weeks • {plan.eventCount ?? 0} Workouts
        </Typography>
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ mb: 2.25 }}
        >
          <Stack direction="row" spacing={0.75} alignItems="center">
            <GroupsOutlinedIcon sx={{ fontSize: 18, color: '#40536b' }} />
            <Typography sx={{ color: '#40536b', fontSize: 14 }}>
              {plan.enrolled ?? 0} Athletes
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <CompletionRing value={completion} />
            <Typography
              sx={{ color: '#0f172a', fontSize: 14, fontWeight: 700 }}
            >
              {completion == null ? '-' : `${completion}% Completion`}
            </Typography>
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Button
            variant="outlined"
            onClick={() => navigate(`/trainer/training_plans/${plan._id}`)}
            sx={{ minWidth: 74 }}
          >
            Edit
          </Button>
          {isDraft ? (
            <Button
              variant="contained"
              disabled={publishing}
              onClick={() => onPublish(plan)}
              sx={{ minWidth: 92, backgroundColor: '#6254e8' }}
            >
              {publishing ? 'Publishing...' : 'Publish'}
            </Button>
          ) : isPrivate ? (
            <Button
              variant="contained"
              sx={{
                minWidth: 112,
                backgroundColor: '#ebe9ff',
                color: '#4338ca',
              }}
            >
              Private
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={() => onAssign(plan)}
              sx={{ minWidth: 92, backgroundColor: '#6254e8' }}
            >
              Assign
            </Button>
          )}
          {!isDraft && !isPrivate && (
            <Button variant="outlined" sx={{ minWidth: 86 }}>
              Preview
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <IconButton
            size="small"
            onClick={featured ? onRemoveFromFeatured : onAddToFeatured}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Stack>
      </CardContent>
    </Card>
  );
};

const TrainingPlanList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = QueryString.parse(window.location.search);
  const trainerId = queryParams.trainerId || null;
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(8);
  const [publishingPlanId, setPublishingPlanId] = useState(null);
  const [assigningPlan, setAssigningPlan] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const { loading, error, data, refetch } = useQuery(GET_TRAINING_PLAN_LIST, {
    variables: { trainerId },
  });
  const [createTrainingPlan] = useMutation(CREATE_TRAINING_PLAN, {
    onCompleted: (data) => {
      if (!data.createTrainingPlan._id) {
        return;
      }
      navigate(`/trainer/training_plans/${data.createTrainingPlan._id}`);
    },
    onError: (error) => {
      enqueueSnackbar(`Error: ${error.message}`, {
        variant: 'error',
        autoHideDuration: 1000,
      });
    },
  });
  const [updateTrainingPlanList] = useMutation(UPDATE_TRAINING_PLAN_LIST, {
    onError: (error) => {
      enqueueSnackbar(`Error: ${error.message}`, {
        variant: 'error',
        autoHideDuration: 1000,
      });
    },
  });
  const [updateTrainingPlan] = useMutation(UPDATE_TRAINING_PLAN, {
    onError: (error) => {
      enqueueSnackbar(`Error: ${error.message}`, {
        variant: 'error',
        autoHideDuration: 1000,
      });
    },
  });
  const [deleteTrainingPlan] = useMutation(DELETE_TRAINING_PLAN, {
    onCompleted: () => {
      refetch();
    },
    onError: (error) => {
      enqueueSnackbar(`Error: ${error.message}`, {
        variant: 'error',
        autoHideDuration: 1000,
      });
    },
  });

  useEffect(() => {
    refetch();
  }, [refetch, location]);

  const plans = data?.trainingPlanList?.trainingPlans ?? [];
  const featuredIds = data?.trainingPlanList?.featuredTrainingPlanIds ?? [];

  const filteredPlans = useMemo(() => {
    return plans.filter((plan) => {
      const matchesSearch = (plan.title ?? '')
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const isFeatured = featuredIds.includes(plan._id);
      const matchesFilter =
        filter === 'all' ||
        (filter === 'published' && `${plan.status}` === '1') ||
        (filter === 'drafts' && `${plan.status}` === '0') ||
        (filter === 'featured' && isFeatured) ||
        (filter === 'private' && `${plan.status}` === '2') ||
        (filter === 'assigned' && (plan.enrolled ?? 0) > 0);
      return matchesSearch && matchesFilter;
    });
  }, [featuredIds, filter, plans, searchTerm]);
  const totalPages = Math.max(1, Math.ceil(filteredPlans.length / perPage));
  const currentPage = Math.min(page, totalPages);
  const pageStart =
    filteredPlans.length === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const pageEnd = Math.min(currentPage * perPage, filteredPlans.length);
  const paginatedPlans = filteredPlans.slice(pageStart - 1, pageEnd);

  useEffect(() => {
    setPage(1);
  }, [filter, perPage, searchTerm]);

  const handleCreate = (event) => {
    event.preventDefault();
    createTrainingPlan({
      variables: {
        input: {
          title: 'Untitled',
        },
      },
    });
  };

  const handleAddToFeatured = async (id) => {
    if (!data || !id) {
      return;
    }
    enqueueSnackbar('Saving...', { autoHideDuration: 1000 });

    const trainingPlanIds = plans.map((i) => i._id);
    const newFeaturedTrainingPlanIds = Array.from(
      new Set([...featuredIds, id])
    ).sort((firstPlanId, secondPlanId) =>
      trainingPlanIds.indexOf(firstPlanId) <
      trainingPlanIds.indexOf(secondPlanId)
        ? -1
        : 1
    );

    updateTrainingPlanList({
      variables: {
        id: data.trainingPlanList._id,
        input: {
          featuredTrainingPlanIds: newFeaturedTrainingPlanIds,
        },
      },
    });
  };

  const handleRemoveFromFeatured = async (id) => {
    if (!data || !id) {
      return;
    }
    enqueueSnackbar('Saving...', { autoHideDuration: 1000 });

    updateTrainingPlanList({
      variables: {
        id: data.trainingPlanList._id,
        input: {
          featuredTrainingPlanIds: featuredIds.filter(
            (planId) => planId !== id
          ),
        },
      },
    });
  };

  const handleDelete = (id) => {
    if (!id || !window.confirm('Delete the item?')) {
      return;
    }
    enqueueSnackbar('Deleting...', { autoHideDuration: 1000 });
    deleteTrainingPlan({ variables: { id } });
  };

  const handlePublish = async (plan) => {
    if (!plan?._id || publishingPlanId) {
      return;
    }

    setPublishingPlanId(plan._id);
    try {
      await updateTrainingPlan({
        variables: {
          id: plan._id,
          input: {
            status: 1,
          },
        },
      });
      enqueueSnackbar('Training plan published.', {
        variant: 'success',
        autoHideDuration: 1000,
      });
      await refetch();
      window.location.reload();
    } finally {
      setPublishingPlanId(null);
    }
  };

  const handleAssignDialogClose = async (trainingPlanSession) => {
    setAssigningPlan(null);

    if (trainingPlanSession?._id) {
      await refetch();
      window.location.reload();
    }
  };

  if (error) {
    return <ServerError message={error.message ?? 'Load failed!'} />;
  }

  if (loading) {
    return <Loading />;
  }

  const totalPlans = plans.length;
  const totalAssigned = plans.reduce(
    (sum, plan) => sum + (plan.enrolled ?? 0),
    0
  );
  const draftCount = plans.filter((plan) => `${plan.status}` === '0').length;
  const avgCompletion =
    plans.length === 0
      ? 0
      : Math.round(
          plans.reduce((sum, plan) => sum + (completionPercent(plan) ?? 0), 0) /
            plans.length
        );

  return (
    <Page title={`Training Plans : ${config.appDisplayName}`}>
      <Container maxWidth={false}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ mb: 4 }}
        >
          <Box>
            <Typography
              sx={{ fontSize: 30, fontWeight: 800, color: '#071120', mb: 1 }}
            >
              Training Plans
            </Typography>
            <Typography sx={{ color: '#40536b', fontSize: 16 }}>
              Create and manage training plans and programs for your athletes.
            </Typography>
          </Box>
          <Button
            component={Link}
            to="#"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            sx={{
              backgroundColor: '#4338ca',
              px: 2.5,
              py: 1.25,
              '&:hover': { backgroundColor: '#3730a3' },
            }}
          >
            Create New Plan
          </Button>
        </Stack>

        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4} lg={2.4}>
            <StatCard
              icon={<AssignmentOutlinedIcon />}
              value={totalPlans}
              label="Total Plans"
              color="#4338ca"
              backgroundColor="#edeaff"
            />
          </Grid>
          <Grid item xs={12} md={4} lg={2.4}>
            <StatCard
              icon={<GroupsOutlinedIcon />}
              value={totalAssigned}
              label="Athletes Enrolled"
              color="#16a34a"
              backgroundColor="#dcfce7"
            />
          </Grid>
          <Grid item xs={12} md={4} lg={2.4}>
            <StatCard
              icon={<TrendingUpIcon />}
              value={`${avgCompletion}%`}
              label="Avg. Completion"
              color="#2f80ed"
              backgroundColor="#e5f1ff"
            />
          </Grid>
          <Grid item xs={12} md={4} lg={2.4}>
            <StatCard
              icon={<CheckCircleOutlineIcon />}
              value={draftCount}
              label="Drafts"
              color="#f97316"
              backgroundColor="#fff0df"
            />
          </Grid>
          <Grid item xs={12} md={4} lg={2.4}>
            <StatCard
              icon={<StarBorderIcon />}
              value={featuredIds.length}
              label="Featured Plans"
              color="#8b5cf6"
              backgroundColor="#f0e7ff"
            />
          </Grid>
        </Grid>

        <Card
          variant="outlined"
          sx={{ borderColor: '#e2e8f0', borderRadius: 1, mb: 2.5 }}
        >
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', lg: 'center' }}
            >
              <TextField
                size="small"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search plans..."
                sx={{ minWidth: { xs: '100%', lg: 300 } }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon sx={{ color: '#0f172a' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <ToggleButtonGroup
                exclusive
                value={filter}
                onChange={(event, value) => value && setFilter(value)}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    px: 2,
                    border: 0,
                    fontWeight: 700,
                    textTransform: 'none',
                  },
                  '& .Mui-selected': {
                    color: '#4338ca !important',
                    backgroundColor: '#ebe9ff !important',
                  },
                }}
              >
                <ToggleButton value="all">All Plans</ToggleButton>
                <ToggleButton value="published">Published</ToggleButton>
                <ToggleButton value="drafts">Drafts</ToggleButton>
                <ToggleButton value="assigned">Assigned</ToggleButton>
                <ToggleButton value="featured">Featured</ToggleButton>
                <ToggleButton value="private">Private</ToggleButton>
              </ToggleButtonGroup>
              <Box sx={{ flex: 1 }} />
              <Select size="small" value="newest" sx={{ minWidth: 176 }}>
                <MenuItem value="newest">Sort by: Newest</MenuItem>
              </Select>
              <ToggleButtonGroup exclusive value="grid" size="small">
                <ToggleButton value="grid">
                  <AppsIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="list">
                  <ListIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </CardContent>
        </Card>

        <Grid container spacing={3}>
          {paginatedPlans.map((plan) => (
            <Grid item xs={12} sm={6} lg={3} key={plan._id}>
              <TrainingPlanCard
                plan={plan}
                featured={featuredIds.includes(plan._id)}
                onDelete={() => handleDelete(plan._id)}
                onAddToFeatured={() => handleAddToFeatured(plan._id)}
                onRemoveFromFeatured={() => handleRemoveFromFeatured(plan._id)}
                onPublish={handlePublish}
                publishing={publishingPlanId === plan._id}
                onAssign={setAssigningPlan}
              />
            </Grid>
          ))}
        </Grid>

        <AssignTrainingPlanDialog
          open={!!assigningPlan}
          trainingPlan={assigningPlan}
          onClose={handleAssignDialogClose}
        />

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ py: 3, color: '#40536b' }}
        >
          <Typography>
            Showing {pageStart} to {pageEnd} of {filteredPlans.length} plans
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              disabled={currentPage === 1}
              onClick={() => setPage((value) => Math.max(1, value - 1))}
              sx={{ minWidth: 34, px: 1 }}
            >
              ←
            </Button>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map(
              (pageNumber) => (
                <Button
                  key={pageNumber}
                  onClick={() => setPage(pageNumber)}
                  variant={pageNumber === currentPage ? 'outlined' : 'text'}
                  sx={{ minWidth: 34, px: 1 }}
                >
                  {pageNumber}
                </Button>
              )
            )}
            <Button
              disabled={currentPage === totalPages}
              onClick={() =>
                setPage((value) => Math.min(totalPages, value + 1))
              }
              sx={{ minWidth: 34, px: 1 }}
            >
              →
            </Button>
          </Stack>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Typography>Show</Typography>
            <Select
              size="small"
              value={perPage}
              onChange={(event) => setPerPage(Number(event.target.value))}
            >
              <MenuItem value={8}>8</MenuItem>
              <MenuItem value={12}>12</MenuItem>
              <MenuItem value={24}>24</MenuItem>
            </Select>
            <Typography>per page</Typography>
          </Stack>
        </Stack>
      </Container>
    </Page>
  );
};

export default TrainingPlanList;
